export function removeSubstring(originalString: string, substringToRemove: string): string {
    return originalString.replace(new RegExp(substringToRemove, 'g'), '');
}

export function truncate(str: string, length = 15): string {
    if (str.length <= length) return str;
    return str.slice(0, length).trimEnd() + ' ...';
}

export function removeTrailingEmptyHtmlTags(html: string): string {
    if (!html) return '';

    let processedHtml = html.trim();
    let previousLength = 0;

    while (processedHtml.length !== previousLength) {
        previousLength = processedHtml.length;

        // Matches trailing empty HTML tags (tags containing only whitespace or <br> tags)
        // Examples that match: "<p></p>", "<div>   </div>", "<p><br></p>", "<div><br/><br/></div>"
        // Examples that don't match: "<p>Hello</p>", "<p></p><p>World</p>"
        processedHtml = processedHtml.replace(
            /<(\w+)[^>]*>(\s*(?:<br\s*\/?>|\s|<\w+[^>]*>\s*(?:<br\s*\/?>|\s)*<\/\w+>\s*)*)\s*<\/\1>\s*$/is,
            ''
        );
    }

    return processedHtml.trim();
}

/**
 * Checks if a string contains HTML tags
 * @param content - The content string to check
 * @returns true if the content contains HTML tags, false otherwise
 */
export function isHtmlContent(content: string | null | undefined): boolean {
    if (!content || typeof content !== 'string') return false;
    return /<[a-z][\s\S]*>/i.test(content);
}

/**
 * Formats file size in bytes to human-readable string
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "1.5 MB", "500 KB")
 */
export function formatFileSize(bytes: number | undefined | null): string {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const maxIndex = sizes.length - 1;
    const index = Math.min(i, maxIndex);
    return Math.round((bytes / Math.pow(k, index)) * 100) / 100 + ' ' + sizes[index];
}

/**
 * Converts URLs in plain text to clickable HTML links
 * Recognizes http://, https://, and www. URLs
 * @param text - The plain text to convert
 * @returns HTML string with URLs converted to links
 */
export function convertUrlsToLinks(text: string | null | undefined): string {
    if (!text || typeof text !== 'string') return '';

    // URL regex pattern: matches http://, https://, or www. URLs
    // Matches URLs like: http://example.com, https://example.com/path, www.example.com
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

    const urlMatches: Array<{ url: string; index: number; length: number }> = [];
    let match;
    while ((match = urlRegex.exec(text)) !== null) {
        urlMatches.push({
            url: match[0],
            index: match.index,
            length: match[0].length,
        });
    }

    // If no URLs found, just escape and return
    if (urlMatches.length === 0) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    // Build HTML string by escaping text and replacing URLs with links
    let result = '';
    let lastIndex = 0;

    urlMatches.forEach((urlMatch) => {
        // Escape text before URL
        const textBefore = text.substring(lastIndex, urlMatch.index);
        const div = document.createElement('div');
        div.textContent = textBefore;
        result += div.innerHTML;

        // Create link for URL
        const href = urlMatch.url.startsWith('http') ? urlMatch.url : `https://${urlMatch.url}`;
        const escapedUrl = urlMatch.url
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
        const escapedHref = href
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');

        result += `<a href="${escapedHref}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-700 hover:underline">${escapedUrl}</a>`;

        lastIndex = urlMatch.index + urlMatch.length;
    });

    // Escape remaining text after last URL
    const textAfter = text.substring(lastIndex);
    const div = document.createElement('div');
    div.textContent = textAfter;
    result += div.innerHTML;

    return result;
}
